import { __awaiter } from '../../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { getClassNames } from '@websolutespa/bom-core';
import { useLabel, useAutosizeTextArea } from '@websolutespa/bom-mixer-hooks';
import { IconLlmVolume, IconLlmVolumeX, IconLlmRefresh, IconLlmPaperclip, IconX, IconLlmMicrophone, IconLlmStop, IconLlmSend, IconWebsolute } from '@websolutespa/bom-mixer-icons';
import { useRef } from 'react';
import { useLlm } from '../../../useLlm/useLlm.js';
import { useLlmView } from '../../../useLlm/useLlmView.js';
import { Disclaimer } from './disclaimer.js';

const Prompt = () => {
  const label = useLabel();
  const textAreaRef = useRef(null);
  const {
    Blob,
    BusyIndicator,
    Suggestions
  } = useLlm(state => state.components);
  const app = useLlm(state => state.app);
  const threadId = useLlm(state => state.threadId);
  const prompt = useLlm(state => state.prompt);
  const upload = useLlm(state => state.upload);
  const messages = useLlm(state => state.messages);
  const streaming = useLlm(state => state.streaming);
  const speakEnabled = useLlm(state => state.speakEnabled);
  const mimeTypes = useLlm(state => state.mimeTypes);
  const storageMode = useLlm(state => state.storageMode);
  const {
    clear,
    setPrompt,
    recognizeStart,
    recognizeStop,
    abort,
    toggleSpeak,
    removeFile,
    hasSpeechRecognitionSupport,
    hasSpeechSynthesisSupport,
    addFile,
    hasUploadSupport
  } = useLlm(state => state.actions);
  const {
    send
  } = useLlmView(state => state.actions);
  useAutosizeTextArea(textAreaRef.current, prompt);
  const onChange = event => {
    var _a;
    const prompt = (_a = event.target) === null || _a === void 0 ? void 0 : _a.value;
    setPrompt(prompt);
  };
  const onSubmit = event => __awaiter(void 0, void 0, void 0, function* () {
    event && event.preventDefault();
    if (!prompt) {
      return;
    }
    yield send(prompt, response => {
      // console.log('Prompt.onChunk', response.chunks);
    }, response => {
      // console.log('Prompt.onEnd', response);
    });
  });
  const onKeyDown = event => {
    if (event.code == 'Enter' && !event.shiftKey) {
      onSubmit(event);
    }
  };
  const hasSpeechSynthesis = hasSpeechSynthesisSupport();
  const hasSpeechRecognition = hasSpeechRecognitionSupport();
  const hasUpload = hasUploadSupport();
  return jsxs("div", {
    className: "llm__prompt",
    children: [jsxs("div", {
      className: "llm__container",
      children: [messages.length === 0 && jsx(Suggestions, {}), jsx("section", {
        id: "llm-prompt",
        children: jsxs("form", {
          className: "llm__prompt-form",
          onSubmit: onSubmit,
          children: [jsxs("div", {
            className: getClassNames('llm__prompt-input', {
              '-speech-synthesis': hasSpeechSynthesis,
              '-speech-recognition': hasSpeechRecognition,
              '-streaming': streaming
            }),
            children: [hasSpeechSynthesis && jsx("button", {
              className: "llm__prompt-speak",
              type: "button",
              "aria-label": speakEnabled ? label('llm.disableSpeak') : label('llm.enableSpeak'),
              title: speakEnabled ? label('llm.disableSpeak') : label('llm.enableSpeak'),
              "aria-pressed": speakEnabled,
              onClick: toggleSpeak,
              children: speakEnabled ? jsx(IconLlmVolume, {}) : jsx(IconLlmVolumeX, {})
            }), storageMode === 'session' && threadId && jsx("button", {
              className: "llm__prompt-speak",
              type: "button",
              "aria-label": label('llm.newThread'),
              title: label('llm.newThread'),
              onClick: clear,
              children: jsx(IconLlmRefresh, {})
            }), jsxs("div", {
              className: "llm__prompt-textareaGroup",
              children: [jsx("label", {
                htmlFor: "llm-prompt-textarea",
                className: "llm__prompt-label",
                children: label('llm.prompt')
              }), jsx("textarea", {
                id: "llm-prompt-textarea",
                className: "llm__prompt-textarea",
                name: "prompt",
                placeholder: app === null || app === void 0 ? void 0 : app.contents.promptPlaceholder,
                onKeyDown: onKeyDown,
                onChange: onChange,
                ref: textAreaRef,
                rows: 1,
                value: prompt,
                "aria-disabled": streaming
              }), jsx(BusyIndicator, {})]
            }), hasUpload && jsxs(Fragment, {
              children: [jsxs("div", {
                className: "llm__prompt-upload-group",
                children: [jsx("div", {
                  className: "llm__prompt-upload",
                  "aria-label": label('llm.upload'),
                  title: label('llm.upload'),
                  children: jsx(IconLlmPaperclip, {})
                }), jsx("input", {
                  className: "llm__prompt-upload-input",
                  type: "file",
                  id: "file-input",
                  accept: mimeTypes,
                  onChange: addFile
                })]
              }), upload && jsxs("button", {
                className: "llm__prompt-upload-file",
                onClick: () => removeFile(),
                title: upload.file.name,
                children: [jsx("span", {
                  children: upload.file.name
                }), jsx(IconX, {}), jsx("img", {
                  src: upload.base64,
                  alt: ""
                })]
              })]
            }), hasSpeechRecognition && jsx("button", {
              className: "llm__prompt-microphone",
              type: "button",
              "aria-label": label('llm.dictate'),
              title: label('llm.dictate'),
              onMouseDown: () => recognizeStart(),
              onMouseUp: () => recognizeStop(),
              onTouchStart: () => recognizeStart(),
              onTouchEnd: () => recognizeStop(),
              children: jsx(IconLlmMicrophone, {})
            })]
          }), streaming ? jsxs("button", {
            className: "llm__prompt-stop",
            type: "button",
            disabled: !threadId,
            "aria-disabled": !threadId,
            "aria-label": label('llm.stopResponse'),
            onClick: abort,
            children: [jsx(Blob, {}), jsx("i", {
              children: jsx(IconLlmStop, {})
            })]
          }) : jsxs("button", {
            className: "llm__prompt-submit",
            type: "submit",
            "aria-disabled": !prompt,
            "aria-label": label('llm.sendPrompt'),
            children: [jsx(Blob, {}), jsx("i", {
              children: jsx(IconLlmSend, {})
            })]
          })]
        })
      })]
    }), jsxs("nav", {
      className: "llm__prompt-footer",
      children: [jsx(Disclaimer, {}), (app === null || app === void 0 ? void 0 : app.contents.disablePoweredBy) !== true && jsxs("a", {
        className: "llm__prompt-powered-by",
        href: "https://www.websolute.com/",
        target: "_blank",
        rel: "noreferrer",
        title: "Powered by Websolute",
        children: [jsx("span", {
          children: "powered by"
        }), " ", jsx(IconWebsolute, {
          "aria-label": "Websolute"
        })]
      })]
    })]
  });
};

export { Prompt };
//# sourceMappingURL=prompt.js.map
